import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import jobApplicationService from '../../../services/jobApplicationService';
import NotificationToast from '../../../components/ui/NotificationToast';

const AssignApplicantModal = ({ isOpen, onClose, applicantIds, hrUsers, onComplete }) => {
  const [selectedHRId, setSelectedHRId] = useState('');
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  if (!isOpen) return null;

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleAssign = async () => {
    if (!selectedHRId) {
      showNotification('Please select an HR personnel', 'error');
      return;
    }

    if (applicantIds.length === 0) {
      showNotification('No applicants selected', 'error');
      return;
    }

    try {
      setLoading(true);
      setNotification(null);

      // Assign each applicant to the selected HR personnel
      const assignPromises = applicantIds.map(async (applicantId) => {
        const result = await jobApplicationService.updateApplication(applicantId, {
          assigned_hr_id: selectedHRId
        });
        return result;
      });

      const results = await Promise.all(assignPromises);
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      if (successCount > 0) {
        showNotification(
          `Successfully assigned ${successCount} applicant(s)${failCount > 0 ? ` (${failCount} failed)` : ''}`,
          failCount > 0 ? 'warning' : 'success'
        );
        if (onComplete) {
          setTimeout(() => {
            onComplete();
          }, 1000);
        }
      } else {
        showNotification('Failed to assign applicants. Please try again.', 'error');
      }
    } catch (error) {
      console.error('Error assigning applicants:', error);
      showNotification('An error occurred while assigning applicants', 'error');
    } finally {
      setLoading(false);
    }
  };

  const selectedHR = hrUsers.find(hr => hr.id === selectedHRId);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-md">
        <NotificationToast 
          message={notification?.message} 
          type={notification?.type} 
          onDismiss={() => setNotification(null)} 
        />

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Assign Applicants to HR</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Assign {applicantIds.length} applicant(s) to an HR personnel
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              HR Personnel <span className="text-red-500">*</span>
            </label>
            <Select
              value={selectedHRId}
              onChange={(value) => setSelectedHRId(value)}
              options={[
                { value: '', label: 'Select HR Personnel' },
                ...hrUsers.map(hr => ({
                  value: hr.id,
                  label: `${hr.full_name}${hr.department ? ` (${hr.department})` : ''}`
                }))
              ]}
              placeholder="Select HR Personnel"
            />
          </div>

          {selectedHR && (
            <div className="bg-muted rounded-lg p-4">
              <p className="text-sm font-medium text-foreground mb-2">Selected HR Personnel:</p>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p><span className="font-medium">Name:</span> {selectedHR.full_name}</p>
                {selectedHR.email && (
                  <p><span className="font-medium">Email:</span> {selectedHR.email}</p>
                )}
                {selectedHR.department && (
                  <p><span className="font-medium">Department:</span> {selectedHR.department}</p>
                )}
              </div>
            </div>
          )}

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Icon name="Info" size={16} className="text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="text-sm text-blue-800 dark:text-blue-300">
                <p className="font-medium mb-1">What happens when you assign:</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Selected applicants will be assigned to the chosen HR personnel</li>
                  <li>Assignment applies to the selected process stage</li>
                  <li>HR personnel will be able to manage these applicants in their dashboard</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleAssign} 
              disabled={loading || !selectedHRId}
              iconName="UserCheck"
              iconPosition="left"
            >
              {loading ? 'Assigning...' : `Assign ${applicantIds.length} Applicant(s)`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignApplicantModal;




